import './AgentChat.css'
import { useEffect, useRef, useState, type CSSProperties } from 'react'
import {
  agentConversationCancelSpec,
  agentConversationGetSpec,
  agentRunSpec,
  type AgentStreamEvent,
  type AgentWidget,
} from 'aevocam-rest-api-spec'
import { toastError } from '@/components/action/Toaster'
import { useApi } from '@/hooks/useApi'
import { Icon } from '@/components/graphics/Icon'
import { cn } from '@/utilities/misc'
import {
  allocMessageId,
  conversationStorageKey,
  THINKING_MODELS,
  turnsToMessages,
  type ChatMessage,
  type ThinkingLevel,
} from './agentChatMessages'
import { AgentChatToolbar } from './AgentChatToolbar'
import { AgentChatHistory } from './AgentChatHistory'
import { AgentChatPrompt, type AgentChatPromptHandle } from './AgentChatPrompt'
import { AgentConversations } from './AgentConversations'

/** Remembers collapsible open state across remounts (keyed by persistenceKey). */
const collapsibleOpenByKey = new Map<string, boolean>()

/** How often to push buffered stream text into React while a reply is in flight. */
const STREAM_FLUSH_MS = 500

type Props = {
  agent: string
  width?: string | number
  context?: Record<string, unknown>
  persistenceKey?: string
  /** Collapse to a thin rail with an expand control */
  collapsible?: boolean
  /** Initial open state when collapsible (default: closed) */
  defaultOpen?: boolean
  /** When agent wants to the article list to get custom filtered */
  setArticleIds?: (articleIds?: string[]) => void
  /** Fires when the article editor's contents need to be reloaded */
  onRefreshEditor?: () => void
  /** Fires after a streamed reply finishes (sending → idle) */
  onReplyDone?: () => void
}

export function AgentChat({
  agent,
  width = '30rem',
  context,
  persistenceKey,
  collapsible = false,
  defaultOpen = false,
  setArticleIds,
  onRefreshEditor,
  onReplyDone,
}: Props) {

  const api = useApi()

  const [prompt, setPrompt] = useState('')
  const [history, setHistory] = useState<ChatMessage[]>([])
  const [thinkingLevel, setThinkingLevel] = useState<ThinkingLevel>('low')
  const [conversationId, setConversationId] = useState<string | undefined>(() => {
    if (!persistenceKey) return undefined
    return localStorage.getItem(conversationStorageKey(persistenceKey)) ?? undefined
  })
  const [sending, setSending] = useState(false)
  const [loadingHistory, setLoadingHistory] = useState(() => !!conversationId)
  const [conversationCostUsd, setConversationCostUsd] = useState<number | undefined>()
  const [showPastConversations, setShowPastConversations] = useState(false)
  const [pinToBottomToken, setPinToBottomToken] = useState(0)
  const [open, setOpen] = useState(() => {
    if (persistenceKey && collapsibleOpenByKey.has(persistenceKey))
      return collapsibleOpenByKey.get(persistenceKey)!
    return defaultOpen
  })
  const [wide, setWide] = useState(false)

  const pageFilter = typeof context?.page === 'string' ? context.page : undefined
  const panelWidth = typeof width === 'number' ? `${width}px` : width

  const promptRef = useRef<AgentChatPromptHandle>(null)
  const wasSendingRef = useRef(false)
  const sendAbortRef = useRef<AbortController | null>(null)
  const stoppingRef = useRef(false)

  useEffect(() => {
    if (!collapsible || !persistenceKey) return
    collapsibleOpenByKey.set(persistenceKey, open)
  }, [collapsible, persistenceKey, open])

  //--------------------------------------------------------------------------------
  // Hydrate from localStorage once per persistence key. Do not key this effect on
  // `api` — useApi()'s identity changes with apiStatus (createGlobalState), and
  // re-fetching would resurrect whatever conversationId was current at mount.
  useEffect(() => {
    if (!persistenceKey) return
    const storageKey = conversationStorageKey(persistenceKey)
    const storedId = localStorage.getItem(storageKey)
    if (!storedId) return
    const id: string = storedId

    let cancelled = false

    async function loadHistory() {
      setLoadingHistory(true)
      try {
        const res = await api.request(agentConversationGetSpec, {
          path: { conversation: id },
        })
        if (cancelled) return
        // New conversation / newer turn.start may have moved the key while we fetched.
        if (localStorage.getItem(storageKey) !== id)
          return

        if (res.status !== 200 || !res.data?.turns) {
          setConversationId(undefined)
          localStorage.removeItem(storageKey)
          toastError(res.error ?? 'Conversation not found')
          return
        }

        setHistory(turnsToMessages(res.data.turns.rows ?? []))
        setConversationCostUsd(res.data.conversationCostUsd)
      } catch (error) {
        if (cancelled) return
        if (localStorage.getItem(storageKey) !== id)
          return
        console.error(error)
        setConversationId(undefined)
        localStorage.removeItem(storageKey)
        toastError('Failed to load conversation')
      } finally {
        if (!cancelled && localStorage.getItem(storageKey) === id)
          setLoadingHistory(false)
      }
    }

    void loadHistory()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- hydrate once; see comment above
  }, [persistenceKey])

  //--------------------------------------------------------------------------------
  // After sending ends, flush reply-done once child widget effects have run
  // (they may have marked a pending editor refresh during fromStream).
  useEffect(() => {
    if (sending) {
      wasSendingRef.current = true
      return
    }
    if (!wasSendingRef.current) return
    wasSendingRef.current = false
    onReplyDone?.()
  }, [sending, onReplyDone])

  //--------------------------------------------------------------------------------
  function handleNewConversation() {
    if (persistenceKey)
      localStorage.removeItem(conversationStorageKey(persistenceKey))
    setConversationId(undefined)
    setHistory([])
    setConversationCostUsd(undefined)
    setPrompt('')
    setShowPastConversations(false)
    setPinToBottomToken(n => n + 1)
    queueMicrotask(() => promptRef.current?.focus())
  }

  //--------------------------------------------------------------------------------
  async function handleSelectPastConversation(id: string) {
    setShowPastConversations(false)
    if (id === conversationId) {
      queueMicrotask(() => promptRef.current?.focus())
      return
    }

    setLoadingHistory(true)
    setHistory([])
    setConversationCostUsd(undefined)
    setPrompt('')
    setPinToBottomToken(n => n + 1)

    try {
      const res = await api.request(agentConversationGetSpec, {
        path: { conversation: id },
      })
      if (res.status !== 200 || !res.data?.turns) {
        toastError(res.error ?? 'Conversation not found')
        setConversationId(undefined)
        if (persistenceKey)
          localStorage.removeItem(conversationStorageKey(persistenceKey))
        return
      }

      setConversationId(id)
      if (persistenceKey)
        localStorage.setItem(conversationStorageKey(persistenceKey), id)
      setHistory(turnsToMessages(res.data.turns.rows ?? []))
      setConversationCostUsd(res.data.conversationCostUsd)
      queueMicrotask(() => promptRef.current?.focus())
    } catch (error) {
      console.error(error)
      toastError('Failed to load conversation')
      setConversationId(undefined)
      if (persistenceKey)
        localStorage.removeItem(conversationStorageKey(persistenceKey))
    } finally {
      setLoadingHistory(false)
    }
  }

  //--------------------------------------------------------------------------------
  function setAgentContent(messageId: number, content: string) {
    setHistory(prev => prev.map(message =>
      message.id === messageId
        ? { ...message, content }
        : message,
    ))
  }

  //--------------------------------------------------------------------------------
  function appendAgentWidget(messageId: number, widget: AgentWidget) {
    setHistory(prev => prev.map(message =>
      message.id === messageId
        ? { ...message, widgets: [...(message.widgets ?? []), widget] }
        : message,
    ))
  }

  //--------------------------------------------------------------------------------
  async function handleStop() {
    if (!sending) return
    stoppingRef.current = true
    if (!conversationId) {
      sendAbortRef.current?.abort()
      return
    }
    try {
      await api.request(agentConversationCancelSpec, {
        path: { conversation: conversationId },
      })
    } catch (error) {
      console.error(error)
      sendAbortRef.current?.abort()
    }
  }

  //--------------------------------------------------------------------------------
  async function handleSend() {
    const text = prompt.trim()
    if (!text || sending || loadingHistory) return

    const agentMessageId = allocMessageId()
    const pendingWidgets: AgentWidget[] = []
    const abort = new AbortController()
    sendAbortRef.current = abort
    stoppingRef.current = false

    // Buffer text/reasoning deltas and flush on an interval so ReactMarkdown
    // is not rebuilt on every token (which can drop toolbar clicks).
    let pendingContent = ''
    let pendingReasoning = ''
    let flushTimer: ReturnType<typeof setTimeout> | null = null
    let contentStarted = false
    let reasoningStarted = false

    function flushStreamBuffers() {
      if (flushTimer != null) {
        clearTimeout(flushTimer)
        flushTimer = null
      }
      const contentDelta = pendingContent
      const reasoningDelta = pendingReasoning
      if (!contentDelta && !reasoningDelta) return
      pendingContent = ''
      pendingReasoning = ''
      setHistory(prev => prev.map(message => {
        if (message.id !== agentMessageId) return message
        return {
          ...message,
          ...(contentDelta ? { content: message.content + contentDelta } : {}),
          ...(reasoningDelta
            ? { reasoning: (message.reasoning ?? '') + reasoningDelta }
            : {}),
        }
      }))
    }

    function scheduleFlush() {
      if (flushTimer != null) return
      flushTimer = setTimeout(() => {
        flushTimer = null
        flushStreamBuffers()
      }, STREAM_FLUSH_MS)
    }

    setHistory(prev => [
      ...prev,
      { id: allocMessageId(), role: 'user', content: text },
      { id: agentMessageId, role: 'agent', content: '' },
    ])
    setPrompt('')
    setSending(true)
    queueMicrotask(() => promptRef.current?.focus())

    try {
      const res = await api.requestStream(agentRunSpec, {
        path: { agent },
        body: {
          conversationId,
          input: text,
          model: THINKING_MODELS[thinkingLevel],
          context,
        },
        signal: abort.signal,
        onEvent: (event: AgentStreamEvent) => {
          switch (event.type) {
            case 'turn.start':
              setConversationId(event.conversationId)
              if (persistenceKey)
                localStorage.setItem(conversationStorageKey(persistenceKey), event.conversationId)
              break
            case 'text.delta':
              pendingContent += event.delta
              if (!contentStarted) {
                contentStarted = true
                flushStreamBuffers()
              } else {
                scheduleFlush()
              }
              break
            case 'turn.done': {
              if (flushTimer != null) {
                clearTimeout(flushTimer)
                flushTimer = null
              }
              const contentDelta = pendingContent
              const reasoningDelta = pendingReasoning
              pendingContent = ''
              pendingReasoning = ''
              setConversationId(event.conversationId)
              setConversationCostUsd(event.conversationCostUsd)
              setHistory(prev => prev.map(message =>
                message.id === agentMessageId
                  ? {
                      ...message,
                      content: event.reply || (message.content + contentDelta),
                      ...(reasoningDelta
                        ? { reasoning: (message.reasoning ?? '') + reasoningDelta }
                        : {}),
                      ...(pendingWidgets.length > 0
                        ? { widgets: [...pendingWidgets], fromStream: true }
                        : {}),
                    }
                  : message,
              ))
              break
            }
            case 'error':
              flushStreamBuffers()
              setAgentContent(agentMessageId, `*Error:* ${event.message}`)
              break
            case 'reasoning':
              if (event.status === 'in_progress') {
                pendingReasoning += event.summary
                if (!reasoningStarted) {
                  reasoningStarted = true
                  flushStreamBuffers()
                } else {
                  scheduleFlush()
                }
              }
              break
            case 'widget':
              pendingWidgets.push(event.widget)
              // Attach immediately so placeholder-matched widgets can render
              // while text is still streaming; trailing widgets wait in AgentReply.
              appendAgentWidget(agentMessageId, event.widget)
              break
          }
        },
      })

      if (res.status !== 200 && !stoppingRef.current && !abort.signal.aborted) {
        const message = res.error ?? 'Agent request failed'
        toastError(message)
        flushStreamBuffers()
        setAgentContent(agentMessageId, `*Error:* ${message}`)
      }
    } catch (error) {
      if (stoppingRef.current || abort.signal.aborted) {
        flushStreamBuffers()
        return
      }
      console.error(error)
      toastError('Agent request failed')
      flushStreamBuffers()
      setAgentContent(agentMessageId, '*Error:* Request failed')
    } finally {
      flushStreamBuffers()
      if (sendAbortRef.current === abort) sendAbortRef.current = null
      stoppingRef.current = false
      setSending(false)
    }
  }

  //--------------------------------------------------------------------------------
  const chatBody = (
    <>
      <AgentChatToolbar
        conversationCostUsd={conversationCostUsd}
        thinkingLevel={thinkingLevel}
        onToggleThinking={() => setThinkingLevel(prev => prev === 'low' ? 'high' : 'low')}
        onNewConversation={handleNewConversation}
        canNewConversation={!!conversationId || history.length > 0}
        showPastConversations={showPastConversations}
        onTogglePastConversations={() => setShowPastConversations(prev => !prev)}
        disabled={sending || loadingHistory}
        collapsible={collapsible}
        onHide={() => {
          setWide(false)
          setOpen(false)
        }}
        wide={wide}
        onToggleWide={collapsible ? () => setWide(prev => !prev) : undefined}
      />

      {showPastConversations ? (
        <AgentConversations
          agent={agent}
          page={pageFilter}
          currentConversationId={conversationId}
          onSelect={id => void handleSelectPastConversation(id)}
        />
      ) : (
        <>
          <AgentChatHistory
            history={history}
            setHistory={setHistory}
            sending={sending}
            onStop={() => void handleStop()}
            setArticleIds={setArticleIds}
            onRefreshEditor={onRefreshEditor}
            pinToBottomToken={pinToBottomToken}
          />
          <AgentChatPrompt
            ref={promptRef}
            value={prompt}
            setValue={setPrompt}
            disabled={loadingHistory}
            onSend={() => void handleSend()}
          />
        </>
      )}
    </>
  )

  if (collapsible) {
    return (
      <aside
        className={cn('AgentChat', 'Collapsible', open && 'Open', open && wide && 'Wide')}
        style={{ '--agent-chat-width': panelWidth } as CSSProperties}
      >
        <div className='RailPanel Cutout'>
          <button
            type='button'
            className='RailExpand'
            aria-expanded={open}
            aria-label='Open agent'
            onClick={() => setOpen(true)}
          >
            <Icon icon='riAiAgentFill' />
            <span className='Label'>Agent</span>
          </button>
          <div className='RailBody'>
            {chatBody}
          </div>
        </div>
      </aside>
    )
  }

  return (
    <div
      className='AgentChat'
      style={{ width: '100%', maxWidth: width }}
    >
      {chatBody}
    </div>
  )
}
